import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  FormControl,
  FormLabel,
  TextField,
  Grid,
  Button,
  RadioGroup,
  Radio,
  FormControlLabel,
  IconButton,
  Divider,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import CloseIcon from "@mui/icons-material/Close";
import { Business } from "../../../constants/business.constant";

interface AddBusinessDialogProps {
  open: boolean;
  onClose: () => void;
  editingBusiness: Business | null;
  formik: any;
  loading?: boolean;
}

const AddBusinessDialog: React.FC<AddBusinessDialogProps> = ({
  open,
  onClose,
  editingBusiness,
  formik,
  loading = false,
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={fullScreen}
      sx={{
        "& .MuiDialog-paper": {
          m: { xs: 0, sm: 2 },
          width: { xs: "100%", sm: "calc(100% - 64px)" },
          maxHeight: { xs: "100%", sm: "calc(100% - 64px)" },
        },
      }}
    >
      <DialogTitle
        sx={{
          px: { xs: 2, sm: 3 },
          py: { xs: 1.5, sm: 2 },
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography
          variant={fullScreen ? "h6" : "h5"}
          component="span"
          className="font-bold text-gray-800"
        >
          {!!editingBusiness ? `Edit Business` : `Add Business`}
        </Typography>
        <IconButton
          aria-label="close"
          onClick={() => onClose()}
          sx={{ color: (theme) => theme.palette.grey[500] }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Divider />

      <DialogContent sx={{ px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 3 } }}>
        <form onSubmit={formik.handleSubmit} id="addBusinessForm">
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid size={{ xs: 12 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <FormLabel sx={{ fontWeight: 600, color: "#374151", mb: 1.5 }}>
                Company Information
              </FormLabel>
              <TextField
                fullWidth
                className="input-field"
                label="Company Name *"
                name="companyName"
                value={formik.values.companyName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.companyName &&
                  Boolean(formik.errors.companyName)
                }
                helperText={
                  formik.touched.companyName && formik.errors.companyName
                }
              />
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <FormLabel
                sx={{
                  fontWeight: 600,
                  color: "#374151",
                  mb: 1.5,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <LocationOnIcon
                  sx={{ fontSize: 20, mr: 1, color: "#059669" }}
                />
                Business Address
              </FormLabel>
              <TextField
                fullWidth
                className="input-field"
                label="Street Address *"
                name="street"
                value={formik.values.street}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.street && Boolean(formik.errors.street)}
                helperText={formik.touched.street && formik.errors.street}
                sx={{ mb: 2 }}
              />
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    className="input-field"
                    label="City *"
                    name="city"
                    value={formik.values.city}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.city && Boolean(formik.errors.city)}
                    helperText={formik.touched.city && formik.errors.city}
                  />
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <TextField
                    fullWidth
                    className="input-field"
                    label="State *"
                    name="state"
                    value={formik.values.state}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.state && Boolean(formik.errors.state)}
                    helperText={formik.touched.state && formik.errors.state}
                  />
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <TextField
                    fullWidth
                    className="input-field"
                    label="Zip Code *"
                    name="zipCode"
                    value={formik.values.zipCode}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.zipCode && Boolean(formik.errors.zipCode)
                    }
                    helperText={formik.touched.zipCode && formik.errors.zipCode}
                  />
                </Grid>
              </Grid>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              className="input-field"
              label="Ownership Details"
              name="ownershipDetails"
              value={formik.values.ownershipDetails}
              onChange={formik.handleChange}
              multiline
              rows={3}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              className="input-field"
              label="TAX ID / EIN"
              name="taxIdEin"
              value={formik.values.taxIdEin}
              onChange={formik.handleChange}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Typography variant="subtitle1">Business Registration</Typography>
            <RadioGroup
              name="registrationType"
              value={formik.values.registrationType}
              onChange={formik.handleChange}
            >
              <FormControlLabel
                value="Registered"
                control={<Radio />}
                label="Registered"
              />
              <FormControlLabel
                value="LLC or Corporation to be formed"
                control={<Radio />}
                label="LLC or Corporation to be formed"
              />
            </RadioGroup>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Typography variant="subtitle1">Loan Purpose</Typography>
            <RadioGroup
              name="businessPurpose"
              value={formik.values.businessPurpose}
              onChange={formik.handleChange}
            >
              <FormControlLabel
                value="Purchase"
                control={<Radio />}
                label="Purchase"
              />
              <FormControlLabel
                value="Refinance"
                control={<Radio />}
                label="Refinance"
              />
              <FormControlLabel
                value="Startup"
                control={<Radio />}
                label="Startup"
              />
            </RadioGroup>
          </Grid>
        </Grid>
      </form>
    </DialogContent>
      <DialogActions
        sx={{
          px: { xs: 2, sm: 3 },
          py: { xs: 1.5, sm: 2 },
          flexDirection: { xs: "column-reverse", sm: "row" },
          gap: { xs: 1, sm: 0 },
          "& > :not(style) ~ :not(style)": {
            ml: { xs: 0, sm: 1 },
          },
        }}
      >
        <Button
          color="error"
          variant="outlined"
          onClick={() => onClose()}
          startIcon={<CloseIcon />}
          fullWidth={fullScreen}
          sx={{ borderRadius: 2, fontWeight: 600 }}
        >
          Cancel
        </Button>
        <Button
          form="addBusinessForm"
          type="submit"
          variant="contained"
          color="primary"
          startIcon={
            loading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <SaveIcon />
            )
          }
          disabled={loading}
          fullWidth={fullScreen}
          sx={{ borderRadius: 2, fontWeight: 600 }}
        >
          {!!editingBusiness ? "Update" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddBusinessDialog;
